import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import updateUserValidation from '../../validation/update-user-validation.js';
import ErrorMessage from '../common/ErrorMessage.jsx';
import SuccessMessage from '../common/SuccessMessage.jsx';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import useUsers from '../../hooks/useUsers.js';

const UpdateSignatureForm = ({ objectToUpdate }) => {
    const { error, loading, updateUser, user } = useUsers();

    const formik = useFormik({
        initialValues: objectToUpdate,
        validationSchema: updateUserValidation,
        onSubmit: async (values) => {
            const phoneHasPlusOneInForm = String(values.phone ?? '').startsWith(
                '+1'
            );
            const extValue = phoneHasPlusOneInForm
                ? ''
                : String(values.ext ?? '').trim();
            const modifiedValues = {
                name: values.name,
                title: values.title,
                phone: values.phone || '',
                ext: extValue ? parseInt(extValue, 10) : null
            };
            await updateUser(objectToUpdate.id, modifiedValues);
        }
    });

    const phoneHasPlusOne = String(formik.values.phone ?? '').startsWith('+1');

    const formatPhoneNumber = (value) => {
        if (!value) return value;

        // Remove all non-numeric characters except "+"
        const phoneNumber = value.replace(/[^\d+]/g, '');

        // Format U.S.-specific numbers with +1 country code
        if (phoneNumber.startsWith('+1')) {
            const areaCode = phoneNumber.slice(2, 5);
            const prefix = phoneNumber.slice(5, 8);
            const lineNumber = phoneNumber.slice(8, 12);
            if (phoneNumber.length <= 5) return `+1 (${areaCode}`;
            if (phoneNumber.length <= 8) return `+1 (${areaCode}) ${prefix}`;
            return `+1 (${areaCode}) ${prefix}-${lineNumber}`;
        }

        // Format without country code as (XXX) XXX-XXXX
        if (!phoneNumber.startsWith('+')) {
            const areaCode = phoneNumber.slice(0, 3);
            const prefix = phoneNumber.slice(3, 6);
            const lineNumber = phoneNumber.slice(6, 10);
            if (phoneNumber.length <= 3) return `(${areaCode}`;
            if (phoneNumber.length <= 6) return `(${areaCode}) ${prefix}`;
            return `(${areaCode}) ${prefix}-${lineNumber}`;
        }

        // Return other international formats as-is
        return phoneNumber;
    };

    return (
        <>
            <form onSubmit={formik.handleSubmit}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        marginTop: '5em'
                    }}>
                    <h4>
                        {objectToUpdate.isActive
                            ? 'Update your signature'
                            : 'Activate your signature'}
                    </h4>
                    <p>
                        (Enter info exactly as you want it to appear on your
                        signature)
                    </p>

                    <label htmlFor="name">First and Last Name</label>
                    <input
                        id="name"
                        placeholder="First and Last Name"
                        className="create-input"
                        {...formik.getFieldProps('name')}
                    />
                    {formik.touched.name && formik.errors.name && (
                        <ErrorMessage message={formik.errors.name} />
                    )}

                    <label htmlFor="title">
                        Position in eAssist (ex: Success Consultant)
                    </label>
                    <input
                        id="title"
                        placeholder="Title"
                        className="create-input"
                        {...formik.getFieldProps('title')}
                    />
                    {formik.touched.title && formik.errors.title && (
                        <ErrorMessage message={formik.errors.title} />
                    )}

                    <label htmlFor="phone">Phone number (optional)</label>
                    <input
                        id="phone"
                        placeholder="e.g. (801) 123-1234 or +1 (801)-123-1234"
                        className="create-input"
                        {...formik.getFieldProps('phone')}
                        onChange={(e) => {
                            const formattedPhone = formatPhoneNumber(
                                e.target.value
                            );
                            formik.setFieldValue('phone', formattedPhone);
                            if (formattedPhone.startsWith('+1')) {
                                formik.setFieldValue('ext', '');
                            }
                        }}
                    />
                    {formik.touched.phone && formik.errors.phone && (
                        <ErrorMessage message={formik.errors.phone} />
                    )}

                    <label htmlFor="ext">Extension (optional)</label>
                    {phoneHasPlusOne && (
                        <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                            This phone number already includes +1, remove to add
                            an extension.
                        </p>
                    )}
                    <input
                        id="ext"
                        placeholder="1 to 5 digits"
                        className="create-input"
                        {...formik.getFieldProps('ext')}
                        disabled={phoneHasPlusOne}
                        onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, '');
                            if (value.length <= 5) {
                                formik.setFieldValue('ext', value);
                            }
                        }}
                    />
                    {formik.touched.ext && formik.errors.ext && (
                        <ErrorMessage message={formik.errors.ext} />
                    )}

                    <button
                        className="btn btn-primary btn-block"
                        style={{ marginBottom: '3em' }}
                        type="submit">
                        {objectToUpdate.isActive
                            ? 'Make Changes'
                            : 'Activate Signature'}
                    </button>
                </div>
            </form>
            <br />
            <br />
            {loading && <LoadingSpinner />}
            {Object.keys(formik.errors).length !== 0 && (
                <ErrorMessage message="There are form errors above, please fix." />
            )}
            {error && <ErrorMessage message={error.message} />}
            {user && objectToUpdate.isActive && (
                <SuccessMessage message="Signature has been updated!" />
            )}
            {user && !objectToUpdate.isActive && (
                <SuccessMessage message="Signature has been activated!" />
            )}
        </>
    );
};

UpdateSignatureForm.propTypes = {
    objectToUpdate: PropTypes.object.isRequired
};

export default UpdateSignatureForm;
